import type { JSX } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { debounce, noop } from "lodash";

import { useTranslate } from "@similarweb/i18n-provider";
import { ChipItemTableCell, PlainTooltip, IconButton } from "@similarweb/ui-components";

import { EditTagsModal } from "app/pages/digital-marketing/keywords-campaigns/shared/components";
import { trackEditTagsModalOpen } from "app/pages/digital-marketing/keywords-campaigns/shared/components/EditTagsModal/tracking";

import { DEFAULT_PADDING, EDIT_ICON_WIDTH, MAX_CHARACTERS_IN_CHIPS, CHIP_PADDINGS, DEFAULT_GAP } from "./constants";
import {
    ActionButtonWrapper,
    ChipsContainer,
    ChipsTooltipWrapper,
    ChipsWrapper,
    EditIconContainer,
    MissingChipsItemTooltip,
} from "./styles";
import type { TagsOfKeywordCellSingleItem, ITagsOfKeywordCellProps } from "./types";

const preventDefault = (e: Event) => {
    //this function must be outside of the component so the removeEventListener will work
    e.preventDefault();
};

export const TagsOfKeywordCell = (props: ITagsOfKeywordCellProps) => {
    const {
        value: incomingValue = [],
        width = 100,
        row,
        isEditAllowed = true,
        onOpenEditTagsWindow = noop,
        onUpdateTags = () => void 0,
        allTags,
        maxTextLengthPerChip = MAX_CHARACTERS_IN_CHIPS,
    } = props;

    const { translate } = useTranslate();
    const value = useMemo(() => incomingValue.sort((a, b) => a.text.localeCompare(b.text)), [incomingValue]);

    const elementRef = useRef<HTMLDivElement>(null);
    const [isOnEditMode, setIsOnEditMode] = useState<boolean>(false);
    const cellPaddingsToReduce = isEditAllowed ? DEFAULT_PADDING + EDIT_ICON_WIDTH : DEFAULT_PADDING;
    const initialCellWidthWithoutPadding = width - cellPaddingsToReduce;
    const canvas = document.createElement("canvas");

    const getInformativeChipsTooltip = (indexOfInformativeChips: number): JSX.Element => {
        const chipsToPresentInTooltip = value
            .filter((_chips, index) => index >= indexOfInformativeChips)
            .map((chips) => chips.text);
        return (
            <ChipsTooltipWrapper>
                {chipsToPresentInTooltip.map((item) => {
                    return <MissingChipsItemTooltip key={`tooltip-${item}`}>{item}</MissingChipsItemTooltip>;
                })}
            </ChipsTooltipWrapper>
        );
    };

    const addInformativeChip = (
        availableItemsLength: number,
        chipsToAdd: TagsOfKeywordCellSingleItem[],
        cellWidthWithoutPadding = 0,
    ) => {
        const context = canvas.getContext("2d");
        if (!context) {
            return chipsToAdd;
        }
        context.font = "13px Roboto";
        const amountOfMissingChips = value.length - chipsToAdd.length;
        const infoChipText = `+${amountOfMissingChips}`;
        const informativeChipWidth = context.measureText(infoChipText).width + CHIP_PADDINGS;
        const cellHasSpaceForInformativeChip =
            availableItemsLength + informativeChipWidth < (width || cellWidthWithoutPadding);
        let informativeChips: TagsOfKeywordCellSingleItem;

        if (cellHasSpaceForInformativeChip) {
            informativeChips = {
                text: infoChipText,
                tooltipComponent: getInformativeChipsTooltip(chipsToAdd.length),
            };
            return [...chipsToAdd, informativeChips];
        } else {
            informativeChips = {
                text: `+${amountOfMissingChips + 1}`,
                tooltipComponent: getInformativeChipsTooltip(value.length - amountOfMissingChips - 1),
            };
            chipsToAdd[chipsToAdd.length - 1] = informativeChips;
            return chipsToAdd;
        }
    };

    const initCellChips = (cellWidthWithoutPadding: number) => {
        let availableItemsLength = 0;
        const chipsToAdd: TagsOfKeywordCellSingleItem[] = [];
        const context = canvas.getContext("2d");
        if (!context) {
            return [];
        }
        context.font = "13px Roboto";
        for (let chipIndex = 0; chipIndex < value.length; chipIndex++) {
            const chipWidth =
                context.measureText(value[chipIndex].text?.slice(0, maxTextLengthPerChip)).width + CHIP_PADDINGS;

            const chipWidthFits = availableItemsLength + chipWidth + DEFAULT_GAP < cellWidthWithoutPadding;

            if (chipWidthFits || chipIndex === 0) {
                availableItemsLength += chipWidth;
                chipsToAdd.push(value[chipIndex]);
            } else {
                break;
            }
        }

        if (chipsToAdd.length < value.length && value.length > 1) {
            return addInformativeChip(availableItemsLength, chipsToAdd, width);
        } else {
            return chipsToAdd;
        }
    };

    const dataAsChipsItems = useMemo(() => {
        return initCellChips(initialCellWidthWithoutPadding);
    }, [value, initialCellWidthWithoutPadding]);

    const updateChips = () => {
        const newCellWidth = width || (elementRef?.current?.parentElement?.clientWidth ?? 0) - cellPaddingsToReduce;
        initCellChips(newCellWidth);
    };

    useEffect(() => {
        if (width) {
            return;
        }
        const debounceUpdateIsTooltipEnabled = debounce(updateChips, 5);
        window.addEventListener("swColumnsResize", debounceUpdateIsTooltipEnabled);
        return () => window.removeEventListener("swColumnsResize", debounceUpdateIsTooltipEnabled);
    }, []);

    useEffect(() => {
        //we would like to block scrolling once the popup is open
        const tableElement = document.getElementsByClassName("swReactTable-wrapper")[0];

        if (isOnEditMode && tableElement) {
            onOpenEditTagsWindow();
            tableElement?.addEventListener("wheel", preventDefault, { capture: true });
        } else {
            tableElement?.removeEventListener("wheel", preventDefault, { capture: true });
        }
    }, [isOnEditMode]);

    const handleOpenEditTagsModal = useCallback(() => {
        setIsOnEditMode(true);
        trackEditTagsModalOpen();
    }, []);

    const getEditIcon = (): JSX.Element => {
        const isEmptyCell = value?.length === 0;
        return (
            <ActionButtonWrapper className={"actionBtn"}>
                <div onClick={handleOpenEditTagsModal}>
                    {isEmptyCell ? (
                        <ChipItemTableCell
                            text={translate("table.cell.chipsCell.addTag")}
                            actionIconName="add"
                            iconSize="xs"
                        />
                    ) : (
                        <IconButton
                            iconName="edit-icon"
                            type="flat"
                            height="24px"
                            iconSize="xs"
                            width="24px"
                        />
                    )}
                </div>
            </ActionButtonWrapper>
        );
    };

    const renderedChips = useMemo(() => {
        if (!dataAsChipsItems) {
            return [];
        }
        return dataAsChipsItems.map((item, index) => {
            return (
                <div key={`chip-${index}-${row.keyword}`}>
                    <PlainTooltip
                        enabled={!!item?.tooltipComponent}
                        tooltipContent={item?.tooltipComponent}
                        maxWidth={undefined}
                        key={`key${row.keyword}-${index}`}
                    >
                        <ChipsWrapper>
                            <ChipItemTableCell
                                text={item?.text}
                                hideActionButton={true}
                                maxAmountOfCharacters={maxTextLengthPerChip}
                            />
                        </ChipsWrapper>
                    </PlainTooltip>
                </div>
            );
        });
    }, [dataAsChipsItems]);

    if (!value) {
        return null;
    }

    return (
        <ChipsContainer
            ref={(e) => {
                elementRef.current = e;
            }}
            id={`${row.keyword}-tags`}
        >
            {renderedChips}
            <EditIconContainer>{!isOnEditMode && isEditAllowed ? getEditIcon() : null}</EditIconContainer>
            {isEditAllowed ? (
                <EditTagsModal
                    isOpen={isOnEditMode}
                    onClose={() => setIsOnEditMode(false)}
                    selectedKeywords={[row]}
                    allTags={allTags}
                    onUpdateTags={onUpdateTags}
                />
            ) : undefined}
        </ChipsContainer>
    );
};
